import { PageboxQrComplete } from "../PageboxQrComplete";
import { pageboxAuthPath, returnToPageboxLogin } from "../pageboxNavigation";
import { PageboxAccountNotice } from "../PageboxAccountNotice";
import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { customConfirm } from "@/ui/confirm";
import { usePageScale } from "@/ui/usePageScale";

const BASE = {
    W: 1264,
    MARGIN_X: 108,
    GAP_HDR_TO_FIRST: 40,

    HDR_H: 96,
    HDR_FZ: 40,
    BACK_SIZE: 56,

    INPUT_W: 1048,
    INPUT_H: 124,
    INPUT_FZ: 35,
    ROW_GAP: 64,

    BTN_W: 724,
    BTN_H: 124,
    BTN_FZ: 40,
    GAP_TO_BTN: 140,
} as const;

function useScale() {
    const { s, px } = usePageScale({ baseW: BASE.W, max: 1.2 });
    return { s, spx: px };
}

export default function ResetPasswordPage() {
    const { s, spx } = useScale();
    const nav = useNavigate();
    const loc = useLocation() as any;
    const [searchParams] = useSearchParams();
    const isPagebox = searchParams.get("app_id") === "pagebox";
    const qrEntry = isPagebox && searchParams.get("entry") === "qr";
    const [completed, setCompleted] = useState(false);

    const [resetToken, setResetToken] = useState<string | null>(null);
    const [returnUrl, setReturnUrl] = useState("");
    const [pw, setPw] = useState("");
    const [pw2, setPw2] = useState("");
    const [showPw, setShowPw] = useState(false);
    const [showPw2, setShowPw2] = useState(false);
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState<string | null>(null);

    useEffect(() => {
        const t = String(loc.state?.reset_token || "") || sessionStorage.getItem("bt.pwreset.token");
        if (!t) {
            nav(isPagebox ? pageboxAuthPath("/auth/find-account", qrEntry) : "/auth/login", { replace: true });
            return;
        }
        setResetToken(t);
        setReturnUrl(String(loc.state?.returnUrl || sessionStorage.getItem("bt.pwreset.return_url") || ""));
    }, [loc.state, nav, isPagebox, qrEntry]);

    const pwPolicyOk = useMemo(() => /^(?=.*[A-Za-z])(?=.*\d).{4,20}$/.test(pw), [pw]);
    const pwMatchOk = useMemo(() => pw2 === pw && pw.length > 0, [pw, pw2]);
    const canSubmit = !!resetToken && pwPolicyOk && pwMatchOk && !busy;

    const Label = ({ children }: { children: React.ReactNode }) => (
        <div style={{ fontSize: spx(36), fontWeight: 900, color: "#111", marginBottom: spx(12) }}>{children}</div>
    );

    const onSubmit = async () => {
        if (!resetToken) return;

        setBusy(true);
        setErr(null);

        try {
            const r = await fetch("/api/password/reset", {
                method: "POST",
                headers: { "Content-Type": "application/json", Accept: "application/json" },
                body: JSON.stringify({ reset_token: resetToken, new_password: pw }),
            });

            const data = (await r.json().catch(() => ({}))) as any;
            if (!r.ok) {
                const code = String(data?.error || "");
                if (code === "INVALID_PASSWORD_POLICY") {
                    setErr("비밀번호는 4~20자, 영문+숫자를 포함해야 합니다.");
                    return;
                }
                if (code === "TOKEN_ALREADY_USED") {
                    setErr("요청이 만료되었거나 이미 사용되었습니다. 다시 시도해주세요.");
                    return;
                }
                setErr("비밀번호 재설정에 실패했습니다. 다시 시도해주세요.");
                return;
            }

            sessionStorage.removeItem("bt.pwreset.token");
            sessionStorage.removeItem("bt.pwreset.return_url");
            const u = sessionStorage.getItem("bt.pwreset.username") || "";
            sessionStorage.removeItem("bt.pwreset.username");
            if (qrEntry) {
                setCompleted(true);
                return;
            }
            await customConfirm("비밀번호가 성공적으로 변경되었습니다. 로그인해주세요.", "확인");
            if (isPagebox) await returnToPageboxLogin();
            else if (returnUrl) window.location.href = returnUrl;
        } catch {
            setErr("비밀번호 재설정에 실패했습니다. 다시 시도해주세요.");
        } finally {
            setBusy(false);
        }
    };

    if (completed && qrEntry) return <PageboxQrComplete password />;

    return (
        <div style={{ minHeight: "100vh", display: "grid", placeItems: "start center", width: "100%" }}>
            <div style={{ width: spx(BASE.W), background: "#fff" }}>
                {/* Header */}
                <div
                    style={{
                        display: "flex",
                        alignItems: "center",
                        height: spx(BASE.HDR_H),
                        paddingLeft: spx(BASE.MARGIN_X),
                        paddingRight: spx(BASE.MARGIN_X),
                        borderBottom: "1px solid #E0E0E0",
                        position: "relative",
                    }}
                >
                    <button
                        type="button"
                        onClick={() => isPagebox ? nav(pageboxAuthPath("/auth/find-account", qrEntry), { replace: true }) : nav(-1)}
                        style={{
                            background: "transparent",
                            border: "none",
                            cursor: "pointer",
                            padding: 0,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            width: spx(BASE.BACK_SIZE),
                            height: spx(BASE.BACK_SIZE),
                        }}
                        aria-label="뒤로가기"
                    >
                        <svg width={spx(32)} height={spx(32)} viewBox="0 0 24 24" fill="none" stroke="#111" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                            <polyline points="15 18 9 12 15 6" />
                        </svg>
                    </button>
                    <div
                        style={{
                            position: "absolute",
                            left: "50%",
                            transform: "translateX(-50%)",
                            fontSize: spx(BASE.HDR_FZ),
                            fontWeight: 900,
                            color: "#111",
                        }}
                    >
                        비밀번호 찾기
                    </div>
                </div>

                <div style={{ paddingLeft: spx(BASE.MARGIN_X), paddingRight: spx(BASE.MARGIN_X) }}>
                    <div style={{ height: spx(BASE.GAP_HDR_TO_FIRST) }} />
                    {isPagebox && <PageboxAccountNotice fontSize={spx(26)} password />}

                    <Label>비밀번호</Label>
                    <div style={{ position: "relative", width: spx(BASE.INPUT_W) }}>
                        <input
                            placeholder="비밀번호 입력"
                            type={showPw ? "text" : "password"}
                            autoComplete="new-password"
                            value={pw}
                            onChange={(e) => setPw(e.target.value)}
                            style={{
                                width: "100%",
                                height: spx(BASE.INPUT_H),
                                borderRadius: s(12),
                                padding: `0 ${spx(80)} 0 ${spx(16)}`,
                                border: "1.5px solid #000",
                                fontSize: spx(BASE.INPUT_FZ),
                                fontWeight: 900,
                                boxSizing: "border-box",
                            }}
                        />
                        <button
                            type="button"
                            onClick={() => setShowPw(v => !v)}
                            style={{
                                position: "absolute", right: spx(16), top: "50%",
                                transform: "translateY(-50%)", background: "transparent",
                                border: "none", cursor: "pointer", padding: 0,
                                display: "flex", alignItems: "center",
                            }}
                            aria-label={showPw ? "비밀번호 숨기기" : "비밀번호 보기"}
                        >
                            {showPw ? (
                                <svg width={spx(40)} height={spx(40)} viewBox="0 0 24 24" fill="none" stroke="#555" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94"/>
                                    <path d="M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19"/>
                                    <line x1="1" y1="1" x2="23" y2="23"/>
                                </svg>
                            ) : (
                                <svg width={spx(40)} height={spx(40)} viewBox="0 0 24 24" fill="none" stroke="#555" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                                    <circle cx="12" cy="12" r="3"/>
                                </svg>
                            )}
                        </button>
                    </div>
                    <div style={{ marginTop: spx(12), fontSize: spx(20), color: "#111" }}>4~20자 / 영문+숫자 포함 / 특수문자 사용 가능</div>

                    <div style={{ height: spx(BASE.ROW_GAP) }} />

                    <Label>비밀번호 확인</Label>
                    <div style={{ position: "relative", width: spx(BASE.INPUT_W) }}>
                        <input
                            placeholder="비밀번호 확인"
                            type={showPw2 ? "text" : "password"}
                            autoComplete="new-password"
                            value={pw2}
                            onChange={(e) => setPw2(e.target.value)}
                            style={{
                                width: "100%",
                                height: spx(BASE.INPUT_H),
                                borderRadius: s(12),
                                padding: `0 ${spx(80)} 0 ${spx(16)}`,
                                border: "1.5px solid #000",
                                fontSize: spx(BASE.INPUT_FZ),
                                fontWeight: 900,
                                boxSizing: "border-box",
                            }}
                        />
                        <button
                            type="button"
                            onClick={() => setShowPw2(v => !v)}
                            style={{
                                position: "absolute", right: spx(16), top: "50%",
                                transform: "translateY(-50%)", background: "transparent",
                                border: "none", cursor: "pointer", padding: 0,
                                display: "flex", alignItems: "center",
                            }}
                            aria-label={showPw2 ? "비밀번호 숨기기" : "비밀번호 보기"}
                        >
                            {showPw2 ? (
                                <svg width={spx(40)} height={spx(40)} viewBox="0 0 24 24" fill="none" stroke="#555" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94"/>
                                    <path d="M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19"/>
                                    <line x1="1" y1="1" x2="23" y2="23"/>
                                </svg>
                            ) : (
                                <svg width={spx(40)} height={spx(40)} viewBox="0 0 24 24" fill="none" stroke="#555" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                                    <circle cx="12" cy="12" r="3"/>
                                </svg>
                            )}
                        </button>
                    </div>

                    {err && (
                        <>
                            <div style={{ height: spx(16) }} />
                            <div role="alert" style={{ fontSize: spx(20), color: "#d00" }}>
                                {err}
                            </div>
                        </>
                    )}

                    <div style={{ height: spx(BASE.GAP_TO_BTN) }} />
                    <div style={{ display: "grid", placeItems: "center" }}>
                        <button
                            type="button"
                            onClick={onSubmit}
                            disabled={!canSubmit}
                            style={{
                                width: spx(BASE.BTN_W),
                                height: spx(BASE.BTN_H),
                                borderRadius: s(12),
                                background: "#000",
                                color: "#fff",
                                fontSize: spx(BASE.BTN_FZ),
                                fontWeight: 900,
                                border: "none",
                                opacity: !canSubmit ? 0.75 : 1,
                                cursor: !canSubmit ? "not-allowed" : "pointer",
                            }}
                        >
                            {busy ? "처리 중…" : "비밀번호 재설정"}
                        </button>
                    </div>

                    <div style={{ height: spx(40) }} />
                </div>
            </div>
        </div>
    );
}
